let subMenuTimer: ReturnType<typeof setTimeout>;
let currentMainMenuItem: HTMLElement | null;
let mainMenuToggle: HTMLElement | null;
let searchContainer: HTMLElement | null;

function setCollapsed (mainMenuItem: HTMLElement): void {
    if (mainMenuItem === currentMainMenuItem) {
        currentMainMenuItem = null;
    }
    mainMenuItem.classList.remove('expanded');
    document.body.classList.remove('sub-menu-expanded');
    const childNode = mainMenuItem.querySelector('a');
    if (childNode) {
        childNode.setAttribute('aria-expanded', 'false');
    }
}

function setCollapsedWithTimeout (mainMenuItem: HTMLElement): void {
    subMenuTimer = setTimeout(function () {
        setCollapsed(mainMenuItem);
    }, 100);
}

function setExpanded (mainMenuItem: HTMLElement): void {
    if (currentMainMenuItem) {
        setCollapsed(currentMainMenuItem);
    }

    currentMainMenuItem = mainMenuItem;
    mainMenuItem.classList.add('expanded');
    document.body.classList.add('sub-menu-expanded');
    const childNode = mainMenuItem.querySelector('a');
    if (childNode) {
        childNode.setAttribute('aria-expanded', 'true');
    }

    // Keep the submenu open
    clearTimeout(subMenuTimer);
}

function toggle (mainMenuItemToggle: HTMLElement): void {
    const mainMenuItem = (mainMenuItemToggle.parentElement as HTMLElement);
    if (mainMenuItem === currentMainMenuItem || mainMenuItemToggle.classList.contains('expanded')) {
        setCollapsed(mainMenuItem);
    } else {
        setExpanded(mainMenuItem);
    }
}

function hasMouseOrKeyboardFocusMovedOutsideMainMenu (event: MouseEvent | FocusEvent): boolean {
    // If we don't have related target info, return true (mouse click outside area)
    if (event.relatedTarget === null) {
        return true;
    }
    // Ignore event if we're still within the same .main-menu
    return (event.target as HTMLElement).closest('.main-menu') !== (event.relatedTarget as HTMLElement)?.closest('.main-menu');
}

function hasMouseOrKeyboardFocusMovedOutsideMainMenuItem (event: MouseEvent | FocusEvent): boolean {
    // If we don't have related target info, return false
    if (event.relatedTarget === null) {
        return false;
    }
    // Ignore event if we're still within the same .main-item
    return (event.target as HTMLElement).closest('.main-item') !== (event.relatedTarget as HTMLElement)?.closest('.main-item');
}

function toggleMainMenu (this: HTMLElement, event: UIEvent): void {
    const isExpanded = this.getAttribute('aria-expanded') || 'false';
    if (isExpanded !== 'true') {
        document.body.classList.add('main-menu-expanded');
        this.setAttribute('aria-expanded', 'true');
    } else {
        document.body.classList.remove('main-menu-expanded');
        this.setAttribute('aria-expanded', 'false');
    }

    if (searchContainer) {
        setCollapsed(searchContainer);
    }

    event.preventDefault();
}

function collapseMainMenu (): void {
    document.body.classList.remove('main-menu-expanded');
    mainMenuToggle?.setAttribute('aria-expanded', 'false');
}

function collapseCurrentSubMenuWithTimeout (): void {
    // We have a click event on the submenu tiles, which requires us to wait for a bit before closing the menu
    // Otherwise, the link never gets opened
    setTimeout(function () {
        if (currentMainMenuItem) {
            setCollapsed(currentMainMenuItem);
        }
    }, 100);
}

function moveFocusToToggle (event: KeyboardEvent | FocusEvent, toggle: HTMLElement | null): void {
    if (toggle && window.getComputedStyle(toggle).display !== 'none') {
        event.preventDefault();
        toggle.focus();
    }
}

function megaMenu (selector: string): void {
    mainMenuToggle = document.querySelector<HTMLElement>(selector + ' nav .toggle');
    searchContainer = document.querySelector<HTMLElement>(selector + ' .search');
    const searchToggle = searchContainer?.querySelector<HTMLElement>('.toggle') || null;
    const mobileQuery = window.matchMedia('(max-width: 768px)'); // test the same ways as in CSS ($breakpointMedium)

    mainMenuToggle?.addEventListener('click', toggleMainMenu);
    searchToggle?.addEventListener('click', collapseMainMenu);
    searchToggle?.addEventListener('touchstart', collapseMainMenu, { passive: true });
    document.querySelector<HTMLElement>(selector + ' .main-menu > li:last-child a')?.addEventListener('focusout', (event: FocusEvent): void => {
        if (hasMouseOrKeyboardFocusMovedOutsideMainMenu(event)) {
            moveFocusToToggle(event, mainMenuToggle);
        }
    });
    document.querySelector<HTMLElement>(selector + ' .search-form button[type=submit]')?.addEventListener('keydown', (event: KeyboardEvent): void => {
        if (event.key === 'Tab' && !event.shiftKey && mobileQuery.matches) {
            moveFocusToToggle(event, searchToggle);
        }
    });

    document.querySelector<HTMLElement>(selector)?.addEventListener('focusout', function (event): void {
        if (hasMouseOrKeyboardFocusMovedOutsideMainMenu(event)) {
            setTimeout(function () {
                collapseCurrentSubMenuWithTimeout();
            }, 100);
        }
    });

    const elementsWithSubmenu = document.querySelectorAll<HTMLAnchorElement>(selector + ' .expandable');
    for (const elementWithSubmenu of elementsWithSubmenu) {
        elementWithSubmenu.addEventListener('focusout', function (event): void {
            // We cast to MouseEvent here, because UIEvent doesn't have the required relatedTarget
            if (hasMouseOrKeyboardFocusMovedOutsideMainMenuItem(event)) {
                // We need a timeout here because activating the link results in focusout, but the javascript for the link handling needs time
                setCollapsedWithTimeout(this);
            }
        });

        // Events toggling the submenu (click / touch)
        const mainMenuLink = elementWithSubmenu.querySelector<HTMLAnchorElement>('a[data-sub-menu-toggle="true"]');
        if (mainMenuLink) {
            mainMenuLink.addEventListener('click', function (event): void {
                toggle(this);
                event.preventDefault();
            });
        }

        // Allow clicking on the whole container, not just the link
        const subElements = elementWithSubmenu.querySelectorAll<HTMLElement>('.sub-item');
        for (const subElement of subElements) {
            subElement.addEventListener('click', function (event): void {
                const subMenuLink = (event.currentTarget as HTMLElement).querySelector<HTMLAnchorElement>('a');
                // Don't follow link if user has created a selection
                if (subMenuLink && window.getSelection()?.toString() === '') {
                    if (event.ctrlKey) {
                        window.open(subMenuLink.href);
                    } else {
                        subMenuLink.click();
                    }
                    event.stopPropagation();
                }
            });
        }
    }

    for (const skipLink of document.querySelectorAll('.skip-link a')) {
        skipLink.addEventListener('click', function (): void {
            collapseMainMenu();
            if (window.location.hash) {
                document.getElementById(window.location.hash.replace('#', ''))?.focus();
            }
        });
    }
}

export default megaMenu;
